<template>
  <v-container class="entity-detail-page">
    <entity-detail-header
      :title="name || 'Consumidor'"
      :subtitle="formatDocument(document)"
      back-label="Voltar para consumidores"
      @back="handleBack"
    >
      <template #badges>
        <status-badge-vue v-if="status" :status="status" />
      </template>
      <template #actions>
        <v-btn
          v-if="phone"
          class="whatsapp-btn"
          depressed
          @click="openWhatsApp"
        >
          <v-icon left small>mdi-whatsapp</v-icon>
          WhatsApp
        </v-btn>
        <template v-if="canReview">
          <button-vue dark color="red" type="repprove" @click="openDialogReprove = true">
            Reprovar
          </button-vue>
          <button-vue dark color="green" type="primary" @click="handleApprove">
            Aprovar
          </button-vue>
        </template>
      </template>
    </entity-detail-header>

    <v-row v-if="loading">
      <v-col cols="12" lg="8">
        <v-skeleton-loader type="card" class="mb-4" />
        <v-skeleton-loader type="card" class="mb-4" />
        <v-skeleton-loader type="card" />
      </v-col>
      <v-col cols="12" lg="4">
        <v-skeleton-loader type="card" />
      </v-col>
    </v-row>

    <v-row v-else>
      <v-col cols="12" lg="8">
        <entity-detail-section title="Dados pessoais" icon="mdi-account-outline">
          <v-row>
            <v-col cols="12" md="6">
              <title-subtitle-vue label="Nome completo" :value="name" />
            </v-col>
            <v-col cols="12" md="3">
              <title-subtitle-vue label="Data de nascimento" :value="birthdate || '—'" />
            </v-col>
            <v-col cols="12" md="3">
              <title-subtitle-vue label="Cadastro" :value="formatDate(createdAt)" />
            </v-col>
          </v-row>
        </entity-detail-section>

        <entity-detail-section title="Contato" icon="mdi-phone-outline">
          <v-row>
            <v-col cols="12" md="6">
              <title-subtitle-vue label="Email" :value="email || '—'" />
            </v-col>
            <v-col cols="12" md="6">
              <title-subtitle-vue label="Telefone" :value="formatPhone(phone)" />
            </v-col>
          </v-row>
        </entity-detail-section>

        <entity-detail-section title="Endereço" icon="mdi-map-marker-outline">
          <v-row>
            <v-col cols="12" md="6">
              <title-subtitle-vue label="Logradouro" :value="street || '—'" />
            </v-col>
            <v-col cols="12" md="2">
              <title-subtitle-vue label="Número" :value="addressNumber || '—'" />
            </v-col>
            <v-col cols="12" md="4">
              <title-subtitle-vue label="Bairro" :value="neighborhood || '—'" />
            </v-col>
            <v-col cols="12" md="4">
              <title-subtitle-vue label="Complemento" :value="complement || '—'" />
            </v-col>
            <v-col cols="12" md="3">
              <title-subtitle-vue label="CEP" :value="formatZipCod(zipCode)" />
            </v-col>
            <v-col cols="12" md="3">
              <title-subtitle-vue label="Cidade" :value="cityName || '—'" />
            </v-col>
            <v-col cols="12" md="2">
              <title-subtitle-vue label="Estado" :value="state || '—'" />
            </v-col>
          </v-row>
        </entity-detail-section>

        <entity-detail-section title="Documentos" icon="mdi-card-account-details-outline">
          <v-row>
            <v-col cols="12">
              <title-subtitle-vue label="CPF" :value="formatDocument(document)" />
            </v-col>
            <v-col cols="12" sm="4">
              <entity-detail-document-image
                v-if="selfImage"
                label="Selfie"
                :src="selfImage"
                @click="handleImageClick(selfImage)"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <entity-detail-document-image
                v-if="frontDocumentImage"
                label="Documento (frente)"
                :src="frontDocumentImage"
                @click="handleImageClick(frontDocumentImage)"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <entity-detail-document-image
                v-if="backDocumentImage"
                label="Documento (verso)"
                :src="backDocumentImage"
                @click="handleImageClick(backDocumentImage)"
              />
            </v-col>
          </v-row>
        </entity-detail-section>
      </v-col>

      <v-col cols="12" lg="4">
        <entity-detail-sidebar-card title="Resumo" icon="mdi-information-outline">
          <div class="sidebar-summary">
            <div class="sidebar-summary__row">
              <span class="sidebar-summary__label">Status</span>
              <status-badge-vue v-if="status" :status="status" />
            </div>
            <div class="sidebar-summary__row">
              <span class="sidebar-summary__label">Cadastro</span>
              <span class="sidebar-summary__value">{{ formatDate(createdAt) }}</span>
            </div>
            <div class="sidebar-summary__row">
              <span class="sidebar-summary__label">CPF</span>
              <span class="sidebar-summary__value">{{ formatDocument(document) }}</span>
            </div>
          </div>
        </entity-detail-sidebar-card>

        <entity-detail-sidebar-card
          v-if="hasIndication"
          title="Indicado por"
          icon="mdi-account-arrow-right-outline"
        >
          <v-skeleton-loader v-if="referredByLoading" type="text@3" />
          <template v-else-if="referredBy">
            <v-chip small outlined color="primary" class="mb-3">
              {{ referredByTypeLabel }}
            </v-chip>
            <title-subtitle-vue label="Nome" :value="referredBy.name" />
            <title-subtitle-vue
              class="mt-3"
              label="Documento"
              :value="formatDocument(referredBy.document)"
            />
            <title-subtitle-vue
              class="mt-3"
              label="Telefone"
              :value="formatPhone(referredBy.phone)"
            />
          </template>
          <p v-else class="sidebar-empty">Indicador não encontrado.</p>
        </entity-detail-sidebar-card>

        <entity-detail-sidebar-card
          v-if="showBalances"
          title="Saldos"
          icon="mdi-wallet-outline"
        >
          <div class="metrics-grid">
            <entity-detail-metric
              label="Saldo atual"
              :value="formatPrice(balance.current)"
              highlight
            />
            <entity-detail-metric
              label="Saldo acumulado"
              :value="formatPrice(balance.accumulated)"
            />
            <entity-detail-metric
              label="BRP"
              :value="String(balance.brpoints)"
            />
          </div>
        </entity-detail-sidebar-card>
      </v-col>
    </v-row>

    <v-dialog v-model="showImageDialog" max-width="900">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center py-3">
          <span>Visualizar documento</span>
          <v-btn icon @click="showImageDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-0">
          <v-img :src="clickedImage" contain max-height="80vh" />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="openDialogReprove" width="560" persistent>
      <v-card>
        <v-card-title class="pb-2">Motivo da reprovação</v-card-title>
        <v-card-text>
          <v-radio-group v-model="reasonReprove" hide-details class="mt-0">
            <v-radio label="Falha em uma das imagens enviadas" value="Falha em uma das imagens enviadas" />
            <v-radio
              label="Informações não condizem com o documento enviado"
              value="Informações não condizem com o documento enviado"
            />
            <v-radio label="Outro" value="Outro" />
          </v-radio-group>
          <v-textarea
            v-if="reasonReprove === 'Outro'"
            class="mt-4"
            outlined
            dense
            hide-details
            label="Descreva o motivo"
            v-model="otherReason"
          />
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn text @click="openDialogReprove = false">Cancelar</v-btn>
          <v-btn
            dark
            color="#e5183d"
            depressed
            :disabled="!reasonReprove || (reasonReprove === 'Outro' && !otherReason)"
            @click="handleReprove"
          >
            Confirmar reprovação
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import ButtonVue from "@/components/button/Button.vue";
import TitleSubtitleVue from "@/components/titleSubtitle/TitleSubtitle.vue";
import StatusBadgeVue from "@/components/statusBadge/StatusBadge.vue";
import EntityDetailHeader from "@/components/entityDetail/EntityDetailHeader.vue";
import EntityDetailSection from "@/components/entityDetail/EntityDetailSection.vue";
import EntityDetailSidebarCard from "@/components/entityDetail/EntityDetailSidebarCard.vue";
import EntityDetailMetric from "@/components/entityDetail/EntityDetailMetric.vue";
import EntityDetailDocumentImage from "@/components/entityDetail/EntityDetailDocumentImage.vue";
import { CUSTOMER_REVIEW_STATUSES } from "@/config/userStatusFilters";
import { GetOnboarding } from "@/services/onboardings/GetOnboarding";
import { UpdateOnboarding } from "@/services/onboardings/UpdateOnboarding";
import { GetPartner } from "@/services/partners/GetPartner";
import { format } from "date-fns";

type ReferredByInfo = {
  name: string;
  document: string;
  phone: string;
};

export default defineComponent({
  components: {
    ButtonVue,
    TitleSubtitleVue,
    StatusBadgeVue,
    EntityDetailHeader,
    EntityDetailSection,
    EntityDetailSidebarCard,
    EntityDetailMetric,
    EntityDetailDocumentImage,
  },

  data() {
    return {
      loading: true,
      showImageDialog: false,
      clickedImage: "",
      openDialogReprove: false,
      reasonReprove: "",
      otherReason: "",
      id: "",
      name: "",
      birthdate: "",
      status: "",
      createdAt: "",
      phone: "",
      email: "",
      street: "",
      addressNumber: "",
      neighborhood: "",
      complement: "",
      zipCode: "",
      cityName: "",
      state: "",
      balance: { accumulated: 0, brpoints: 0, current: 0 },
      document: "",
      frontDocumentImage: "",
      backDocumentImage: "",
      selfImage: "",
      indicatorId: "",
      indicatorType: "",
      referredBy: null as ReferredByInfo | null,
      referredByTypeLabel: "",
      referredByLoading: false,
    };
  },

  computed: {
    hasIndication(): boolean {
      return !!this.indicatorId && !!this.indicatorType;
    },
    canReview(): boolean {
      return CUSTOMER_REVIEW_STATUSES.has(this.status);
    },
    showBalances(): boolean {
      return this.status === "ACTIVE" || this.status === "RE_REGISTRATION";
    },
  },

  methods: {
    async handleLoadCustomer() {
      this.loading = true;
      try {
        const getCustomer = new GetOnboarding();
        const customer = await getCustomer.execute(this.$route.params.id as string);
        this.id = customer.id || "";
        this.name = customer.name || "";
        this.birthdate = customer.birthdate || "";
        this.status = customer.status || "";
        this.createdAt = customer.createdAt || "";
        this.phone = customer.phone || "";
        this.email = customer.email || "";
        this.street = customer.address.street || "";
        this.addressNumber = customer.address.addressNumber || "";
        this.neighborhood = customer.address.neighborhood || "";
        this.complement = customer.address.complement || "";
        this.zipCode = customer.address.zipCode || "";
        this.cityName = customer.address.cityName || "";
        this.state = customer.address.state || "";
        this.balance.accumulated = customer.balance?.accumulated ?? 0;
        this.balance.brpoints = customer.balance?.brpoints ?? 0;
        const rawBalance = customer.balance as { current?: number; othersBalance?: number } | undefined;
        this.balance.current = rawBalance?.current ?? rawBalance?.othersBalance ?? 0;
        this.document = customer.document || "";
        this.backDocumentImage = customer.backDocumentImage || "";
        this.frontDocumentImage = customer.frontDocumentImage || "";
        this.selfImage = customer.selfImage || "";
        this.indicatorId = customer.indicatorId || "";
        this.indicatorType = customer.indicatorType || customer.indicatortype || "";
        await this.loadReferredBy();
      } finally {
        this.loading = false;
      }
    },
    async loadReferredBy() {
      if (!this.indicatorId || !this.indicatorType) {
        this.referredBy = null;
        this.referredByTypeLabel = "";
        return;
      }

      this.referredByLoading = true;
      this.referredBy = null;

      try {
        if (this.indicatorType === "PARTNER") {
          const partner = await new GetPartner().execute(this.indicatorId);
          this.referredBy = {
            name: partner.name || "—",
            document: partner.document || "",
            phone: partner.phone || "",
          };
          this.referredByTypeLabel = "Parceiro";
          return;
        }

        if (this.indicatorType === "CUSTOMER") {
          const indicatorCustomer = await new GetOnboarding().execute(this.indicatorId);
          this.referredBy = {
            name: indicatorCustomer.name || "—",
            document: indicatorCustomer.document || "",
            phone: indicatorCustomer.phone || "",
          };
          this.referredByTypeLabel = "Consumidor";
        }
      } catch {
        this.referredBy = null;
        this.referredByTypeLabel = "";
      } finally {
        this.referredByLoading = false;
      }
    },
    async handleApprove() {
      const updateOnboarding = new UpdateOnboarding();
      await updateOnboarding.execute(this.$route.params.id as string, "ACTIVE");
      this.$store.dispatch("showSnack", {
        message: "Conta aprovada com sucesso",
        color: "green darken-1",
      });
      this.status = "ACTIVE";
      this.handleBack();
    },
    async handleReprove() {
      const reason = this.reasonReprove === "Outro" ? this.otherReason : this.reasonReprove;
      const updateOnboarding = new UpdateOnboarding();
      await updateOnboarding.execute(this.$route.params.id as string, "PENDING", reason);
      this.$store.dispatch("showSnack", {
        message: "Conta reprovada com sucesso",
        color: "green darken-1",
      });
      this.openDialogReprove = false;
      this.handleBack();
    },
    handleBack() {
      const status = this.$route.query.status;
      this.$router.push({
        path: "/customers",
        query: typeof status === "string" ? { status } : undefined,
      });
    },
    handleImageClick(imageUrl: string) {
      this.clickedImage = imageUrl;
      this.showImageDialog = true;
    },
    openWhatsApp() {
      const phoneNumber = this.phone.replace(/\D/g, "");
      window.open(
        `https://wa.me/${phoneNumber}?text=Olá ${this.name}! Tudo bem? Somos da BRPoints.`,
        "_blank",
      );
    },
    formatPrice(value: number): string {
      return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value || 0);
    },
    formatPhone(value: string): string {
      if (!value) return "—";
      return `(${value.substr(0, 2)}) ${value.substr(2, 5)}-${value.substr(7, 8)}`;
    },
    formatDate(date: string): string {
      if (!date) return "—";
      return format(new Date(date), "dd/MM/yyyy HH:mm");
    },
    formatZipCod(cep: string): string {
      return cep ? cep.replace(/(\d{5})(\d{3})/, "$1-$2") : "—";
    },
    formatDocument(document: string): string {
      if (!document) return "—";
      if (document.length === 11) {
        return document.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      }
      if (document.length === 14) {
        return document.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
      }
      return document;
    },
  },

  mounted() {
    void this.handleLoadCustomer();
  },
});
</script>

<style scoped lang="scss">
.entity-detail-page {
  max-width: 1280px;
}

.whatsapp-btn {
  background-color: #25d366 !important;
  color: white !important;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
}

.sidebar-summary__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);

  &:last-child {
    border-bottom: none;
  }
}

.sidebar-summary__label {
  font-size: 0.85rem;
  color: #64748b;
  font-weight: 500;
}

.sidebar-summary__value {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1f243f;
  text-align: right;
}

.sidebar-empty {
  margin: 0;
  color: #94a3b8;
  font-size: 0.9rem;
}

.metrics-grid {
  display: grid;
  gap: 12px;
}
</style>
